import apiClient from '@shared/api/client'
import { assistantsApi } from '@shared/api/assistants'
import { athleteNotesApi } from '@shared/api/athleteNotes'
import { groupNotesApi } from '@shared/api/groupNotes'
import { materialsApi } from '@shared/api/materials'
import { athletesApi } from '@shared/api/athletes'
import { attendanceApi } from '@shared/api/attendance'
import { calendarApi } from '@shared/api/calendar'
import { aiActionsApi } from '@shared/api/aiActionsApi'
import { hubContextApi, type HubContextLinkRequest } from '@shared/api/hubContextApi'
import { chatApi, type ChatChannel, type ChatHistoryMessage } from '@shared/api/chatApi'
import { coachesApi } from '@shared/api/coaches'
import { eventsApi } from '@shared/api/events'
import { monthlyPaymentsApi } from '@shared/api/monthlyPayments'
import { groupsApi, type SchedulePreviewDTO, type ScheduleTimelineDTO, type ScheduleVersionDTO } from '@shared/api/groups'
import { occurrencesApi, type OccurrenceDTO, type OccurrencePreviewDTO } from '@shared/api/occurrences'
import { feedbackApi, type FeedbackMessageDTO, type FeedbackThreadDTO, type FeedbackThreadSummaryDTO } from '@shared/api/feedbackApi'
import {
  deleteOrganization,
  generateInvite,
  getInvites,
  getMembers,
  getOrganization,
  getUserOrganizations,
  leaveOrganization,
  revokeInvite,
  updateOrganization,
  useInvite as acceptInviteCode,
  validateInvite,
} from '@shared/api/organizationApi'
import { sportTypesApi as sportTypesListApi } from '@shared/api/sportTypes'
import { subscriptionApi } from '@shared/api/subscription'
import { promoCodesApi } from '@shared/api/promoCodes'
import { tierChangeApi, type PaymentStatusResponse, type TierChangePreview as TierChangePreviewDTO } from '@shared/api/tierChangeApi'
import { useAuthStore } from '@shared/store/authStore'
import { sportTypesApi } from '@shared/api/sportTypesApi'
import { trainingSessionsApi, type RollCallPreviewDTO } from '@shared/api/trainingSessions'
import { transcribeApi } from '@shared/api/transcribeApi'
import type { GroupSchedule, UpdateAthleteRequest } from '@shared/types/api'
import type { Material as ApiMaterial } from '@shared/types/material'
import type { NoteType as AthleteNoteType } from '@shared/types/athleteNote'
import type { NoteType as GroupNoteType } from '@shared/types/groupNote'
import type { Event as ApiEvent, EventParticipant as ApiEventParticipant } from '@shared/types/event'
import { isOnline, reportNetworkFailure } from '../platform/network'
import { toAiAction } from './aiAction'
import { EVENT_KINDS, fromServerDate, resultText, toServerDate } from './events'
import { addDays, isoDate, parseIsoDate, parseSessionKey, sessionKey } from './schedule'
import {
  SliceApiError,
  type Channels,
  type DeletedNote,
  type Material,
  type Note,
  type CoachCard,
  type ClubEvent,
  type EventInput,
  type EventParticipant,
  type ParticipantPayment,
  type ParticipationStatus,
  type HubMessage,
  type Occurrence,
  type OccurrencePreview,
  type PaymentInfo,
  type PaymentStatus,
  type RollCallPreview,
  type SubscriptionLimitKey,
  type SubscriptionState,
  type TierChangePreview,
  type SchedulePreview,
  type ScheduleTimeline,
  type ScheduleVersion,
  type SessionBoard,
  type SessionSlot,
  type SliceApi,
} from './types'
import type { FeedbackChannel, FeedbackMessage, FeedbackThread, FeedbackThreadSummary } from './types'

/** Где работает пользователь: выбранные клуб и направление. */
export interface LiveScope {
  organizationId: string | null
  assistantId: string | null
  userId?: string | null
}

/** groups.schedule - JSON-строка; битое расписание не роняет экран. */
export function parseGroupSchedule(raw: unknown): GroupSchedule[] {
  if (Array.isArray(raw)) return raw as GroupSchedule[]
  if (typeof raw !== 'string' || !raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/**
 * Пустой список Go-сервер отдаёт как null, а не []. У нового клуба пустых
 * списков много (нет тренеров, записей, приглашений), поэтому каждый список
 * из API проходит через arr().
 */
function arr<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : []
}

interface NoteDTO {
  id: string
  title: string
  content: string
  note_type: string
  created_at: string
  updated_at: string
  author_name?: string | null
  can_edit?: boolean
}

/**
 * Запись из ответа сервера: только то, что показывает экран. Права решает
 * сервер (can_edit); ответ без поля (старый сервер, снимок без сети до
 * обновления) кнопки правки не прячет - сервер всё равно ответит 403.
 */
function toNote(n: NoteDTO): Note {
  return {
    id: n.id,
    title: n.title ?? '',
    content: n.content ?? '',
    note_type: n.note_type || 'general',
    created_at: n.created_at,
    updated_at: n.updated_at,
    author_name: n.author_name || null,
    can_edit: n.can_edit !== false,
  }
}

/** Запись в корзине. */
function toDeletedNote(
  n: NoteDTO & { deleted_at?: string; deleted_by_name?: string | null; purge_at?: string; can_restore?: boolean },
): DeletedNote {
  return {
    ...toNote(n),
    deleted_at: n.deleted_at ?? '',
    deleted_by_name: n.deleted_by_name || null,
    purge_at: n.purge_at ?? '',
    can_restore: n.can_restore !== false,
  }
}

/** Материал из ответа сервера; пустые теги Go отдаёт как null. */
function toMaterial(m: ApiMaterial): Material {
  return {
    id: m.id,
    title: m.title ?? '',
    content: m.content ?? '',
    category: m.category || 'general',
    tags: arr(m.tags),
    sort_order: m.sort_order ?? 0,
    created_at: m.created_at,
    updated_at: m.updated_at,
  }
}

function toSliceError(err: unknown): SliceApiError {
  if (err instanceof SliceApiError) return err
  const e = err as { error?: string; status?: number; code?: string } | undefined
  if (e?.error === 'Network error') {
    // Ответа нет: возможно, пропала сеть - пусть проверка доступности решит.
    reportNetworkFailure()
    return new SliceApiError('network', 'network')
  }
  // nginx на месте, а API лежит или перезапускается: для экрана это тоже «нет связи».
  if (e?.status === 502 || e?.status === 503 || e?.status === 504) {
    reportNetworkFailure()
    return new SliceApiError('bad gateway', 'network')
  }
  // Лимит запросов Штаба за месяц и истёкшая подписка (клуб только для чтения):
  // повтор не поможет, экран объясняет правило, а не «ошибку».
  if (e?.status === 429 && e.code === 'ai_limit_exceeded') return new SliceApiError('ai_limit_exceeded', 'limit')
  // Частота разборов переклички (не лимит тарифа): подождать минуту и повторить.
  if (e?.status === 429 && e.code === 'rate_limited') return new SliceApiError('rate_limited', 'server')
  // Короткая перекличка: пока разбирали, отметки изменились - ничего не записано.
  if (e?.status === 409 && e.code === 'roll_call_stale') return new SliceApiError('roll_call_stale', 'conflict')
  if (e?.status === 423) return new SliceApiError('soft_locked', 'limit')
  if (
    e?.status === 409 &&
    (e.error === 'member limit reached' || e.error === 'organization subscription expired' || e.error === 'athlete limit reached')
  ) {
    return new SliceApiError(e.error, 'limit')
  }
  // Лимит групп и тренеров тарифа (этап 5 шаг 1): правило, а не сбой.
  if (e?.status === 409 && (e.error === 'group limit reached' || e.error === 'coach limit reached')) {
    return new SliceApiError(e.error, 'limit')
  }
  // Лимит спортсменов тарифа (новичок на занятии, новый спортсмен): код, а не
  // текст - текст сервера для людей и может меняться.
  if (e?.status === 409 && e.code === 'athlete_limit_reached') return new SliceApiError('athlete_limit_reached', 'limit')
  // Правка расписания: данные изменились, пока тренер смотрел предпросмотр.
  if (e?.status === 409 && (e.error === 'schedule_changed' || e.error === 'preview_stale')) {
    return new SliceApiError(e.error, 'conflict')
  }
  // Одно занятие: отказ по состоянию занятия (появились отметки, слот занят,
  // занятие отменено) - экран перечитывает предпросмотр и объясняет.
  if (e?.status === 409 && (e.error === 'has_marks' || e.error === 'slot_taken' || e.error === 'cancelled')) {
    return new SliceApiError(e.error, 'conflict')
  }
  // Направление с таким названием уже есть (этап 5 шаг 4): повтор не поможет.
  if (e?.status === 409 && e.error === 'direction name taken') return new SliceApiError('name_taken', 'conflict')
  // Перенос на то же время - не сбой сервера: экран различает его по коду.
  if (e?.status === 400 && e.error === 'same_slot') return new SliceApiError('same_slot', 'server')
  // Чужая запись: править, удалять и возвращать может автор и владелец клуба.
  if (e?.status === 403 && e.code === 'not_note_author') return new SliceApiError('not_note_author', 'forbidden')
  if (e?.status === 404) return new SliceApiError('not found', 'not_found')
  return new SliceApiError(e?.error ?? 'server error', 'server')
}

/** Разбор переклички в форму экрана. Пустые списки Go отдаёт как null. */
export function toRollCallPreview(d: RollCallPreviewDTO): RollCallPreview {
  return {
    understood: !!d.understood,
    rows: arr(d.rows).map((r) => ({
      athleteId: r.athlete_id,
      name: r.name,
      before: r.before || null,
      beforeNotes: r.before_notes || undefined,
      after: r.after || null,
      afterNotes: r.after_notes || undefined,
      changed: !!r.changed,
    })),
    changes: d.changes ?? 0,
    notFound: arr(d.not_found),
    ambiguous: arr(d.ambiguous).map((a) => ({ query: a.query, candidates: arr(a.candidates) })),
    mass: d.mass || undefined,
    blocked: !!d.blocked,
    recorded: !!d.recorded,
    pendingActionId: d.pending_action_id || undefined,
  }
}

function toScheduleVersion(v: ScheduleVersionDTO | null | undefined): ScheduleVersion {
  return { id: v?.id || null, validFrom: v?.valid_from || null, schedule: arr(v?.schedule) }
}

/** Запланированная версия: без id или даты это не версия, а мусор - отбрасываем. */
function toPlanned(list: ScheduleVersionDTO[] | null | undefined) {
  return arr(list)
    .map(toScheduleVersion)
    .filter((v): v is ScheduleVersion & { id: string; validFrom: string } => !!v.id && !!v.validFrom)
}

/** Версии расписания группы в форме экрана. */
export function toScheduleTimeline(t: ScheduleTimelineDTO): ScheduleTimeline {
  return {
    today: t.today,
    defaultFrom: t.default_from || t.today,
    current: toScheduleVersion(t.current),
    planned: toPlanned(t.planned),
  }
}

function toOccurrence(o: OccurrenceDTO): Occurrence {
  return {
    date: o.date,
    startTime: o.start_time,
    endTime: o.end_time ?? '',
    location: o.location || undefined,
    sessionId: o.session_id || undefined,
    cancelled: !!o.cancelled,
    extra: !!o.extra,
    marks: o.marks ?? 0,
  }
}

export function toOccurrencePreview(p: OccurrencePreviewDTO): OccurrencePreview {
  return {
    action: p.action,
    allowed: !!p.allowed,
    reason: p.reason || undefined,
    occurrence: toOccurrence(p.occurrence),
    target: p.target
      ? {
          date: p.target.date,
          startTime: p.target.start_time,
          endTime: p.target.end_time ?? '',
          location: p.target.location || undefined,
          restoresCancelled: !!p.target.restores_cancelled,
        }
      : undefined,
  }
}

/** Предпросмотр правки расписания в форме экрана. Списки - через arr(). */
export function toSchedulePreview(p: SchedulePreviewDTO): SchedulePreview {
  return {
    from: p.from,
    to: p.to,
    changed: !!p.changed,
    items: arr(p.items).map((i) => ({
      date: i.date,
      change: i.change,
      oldStart: i.old_start || undefined,
      oldEnd: i.old_end || undefined,
      oldLocation: i.old_location || undefined,
      newStart: i.new_start || undefined,
      newEnd: i.new_end || undefined,
      newLocation: i.new_location || undefined,
      session: i.session_id ? { id: i.session_id, cancelled: !!i.session_cancelled, marks: i.marks ?? 0 } : undefined,
      outcome: i.outcome,
      newCancelled: !!i.new_session_cancelled,
      later: !!i.later,
    })),
    deleteSessionIds: arr(p.delete_session_ids),
    keptMarked: p.kept_marked ?? 0,
    base: toScheduleVersion(p.base),
    replaced: toPlanned(p.replaced),
    replaceVersionIds: arr(p.replace_version_ids),
    suggestedFrom: p.suggested_from || undefined,
  }
}

const PARTICIPATION: ParticipationStatus[] = ['registered', 'confirmed', 'participated', 'cancelled', 'no_show']
const PAYMENTS: ParticipantPayment[] = ['not_paid', 'paid', 'exempt']

/** Событие API в форме экрана. Неизвестный тип - «Другое», битая дата - событие не показываем. */
export function toClubEvent(e: ApiEvent): ClubEvent | null {
  const start = fromServerDate(e.start_date)
  if (!start) return null
  const end = fromServerDate(e.end_date)
  return {
    id: e.id,
    name: e.name,
    kind: EVENT_KINDS.find((k) => k === e.event_type) ?? 'other',
    start,
    end: end ?? undefined,
    location: e.location || undefined,
    description: e.description || undefined,
    fee: e.payment_amount && Number(e.payment_amount) > 0 ? e.payment_amount : undefined,
    participantCount: typeof e.participant_count === 'number' ? e.participant_count : undefined,
  }
}

function toParticipant(p: ApiEventParticipant): EventParticipant {
  return {
    athleteId: p.athlete_id,
    name: p.athlete_name || 'Спортсмен',
    status: PARTICIPATION.find((s) => s === p.status) ?? 'registered',
    payment: PAYMENTS.find((s) => s === p.payment_status) ?? 'not_paid',
    result: resultText(p.result) || undefined,
  }
}

/** Тело создания и правки события из формы. */
function eventBody(input: EventInput) {
  return {
    name: input.name.trim(),
    event_type: input.kind,
    description: input.description.trim(),
    location: input.location.trim(),
    start_date: toServerDate(input.start),
    end_date: input.end ? toServerDate(input.end) : undefined,
    payment_amount: input.fee.trim() ? input.fee.trim().replace(',', '.') : undefined,
  }
}

/** Сообщение API чата в форме экрана. Действие - только известного вида и непустое. */
export function toHubMessage(m: ChatHistoryMessage): HubMessage {
  const a = m.action
  const pending = arr(a?.pending_action_ids).filter((id) => typeof id === 'string' && id !== '')
  return {
    id: m.id,
    role: m.role,
    text: m.content ?? '',
    status: m.status,
    createdAt: m.created_at,
    action:
      a?.type === 'navigate_athletes' && Array.isArray(a.filter_ids) && a.filter_ids.length > 0
        ? { type: 'navigate_athletes', athleteIds: a.filter_ids, label: a.filter_label ?? '' }
        : undefined,
    pendingActionIds: pending.length > 0 ? pending : undefined,
  }
}

const replyReady = (m: { status: string }) => m.status === 'completed' || m.status === 'failed'

/** Сколько ждать ответ Штаба, прежде чем сказать «пока не получен». */
export const HUB_REPLY_WAIT_MS = 3 * 60_000

/**
 * Чат Штаба в PWA показывает свой разговор: вопросы, заданные в приложении, и
 * ответы на них, а не общую ленту с ботами Telegram и MAX. Записи до появления
 * канала сервер отдаёт вместе с ним - раньше это и был разговор приложения.
 * Вопрос через REST сервер сам помечает каналом web.
 */
export const HUB_CHANNEL: ChatChannel = 'web'

function abortError(): Error {
  return new DOMException('aborted', 'AbortError')
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError())
    const t = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t)
        reject(abortError())
      },
      { once: true },
    )
  })
}

/**
 * Все вызовы API идут через guard: ошибки приводятся к SliceApiError. Без сети
 * вызов не уходит вовсе и сразу падает как 'network' (этап 4, ТЗ стр. 24):
 * изменения офлайн не копятся и не отправляются молча позже - экран честно
 * говорит «Нет сети - не сохранено» и даёт повторить вручную. Чтения без сети
 * сюда не доходят: react-query держит их на паузе.
 */
/** HTTP-статус и текст ошибки API (APIError из api/client) - для разбора по месту. */
/**
 * Отказ по приглашению (этап 5 шаг 4): истёкшее, отозванное и исчерпанное (410)
 * - как ненайденное; уже в клубе и нет мест - отдельно.
 */
function inviteError(err: unknown): unknown {
  const status = httpStatus(err)
  const text = httpError(err)
  if (status === 410) return new SliceApiError(text ?? 'invite gone', 'not_found')
  if (status === 409 && text === 'already a member') return new SliceApiError('already_member', 'conflict')
  if (status === 409 && text === 'organization member limit reached') return new SliceApiError('member limit reached', 'limit')
  if (status === 409 && text === 'organization subscription expired') return new SliceApiError(text, 'limit')
  return err
}

function httpStatus(err: unknown): number | undefined {
  return (err as { status?: number } | undefined)?.status
}
function httpError(err: unknown): string | undefined {
  return (err as { error?: string } | undefined)?.error
}

/** Отказы ручек тарифа: экран различает их по коду в message. */
function tierError(err: unknown): unknown {
  if (err instanceof SliceApiError) return err
  const status = httpStatus(err)
  if (status === 403) return new SliceApiError('forbidden', 'server')
  if (status === 400 && httpError(err) === 'invalid tier') return new SliceApiError('invalid_tier', 'not_found')
  if (status === 409 && httpError(err) === 'payment_required') return new SliceApiError('payment_required', 'conflict')
  if (status === 409 && httpError(err) === 'no_payment_required') return new SliceApiError('no_payment_required', 'conflict')
  if (status === 409 && httpError(err) === 'payment_pending') {
    const id = (err as { context?: { payment_id?: unknown } }).context?.payment_id
    return new SliceApiError('payment_pending', 'conflict', typeof id === 'string' ? id : undefined)
  }
  return err
}

const PAYMENT_STATUSES: PaymentStatus[] = ['pending', 'succeeded', 'failed', 'canceled']

export function toPaymentInfo(p: PaymentStatusResponse & { description?: string; created_at?: string; confirmation_url?: string }): PaymentInfo {
  const status = PAYMENT_STATUSES.find((s) => s === p.status) ?? 'unknown'
  return {
    id: p.id,
    status,
    amount: Number(p.amount) || 0,
    description: p.description ?? '',
    createdAt: p.created_at ?? '',
    paidAt: p.paid_at || undefined,
    confirmationUrl: p.confirmation_url || undefined,
  }
}

export function toTierPreview(p: TierChangePreviewDTO): TierChangePreview {
  const charge = Number(p.charge_amount) || 0
  return {
    tier: p.new_tier,
    currentTier: p.current_tier,
    kind: charge > 0 ? 'payment' : p.trial_days_granted > 0 ? 'trial' : 'free',
    charge,
    price: Number(p.new_price) || 0,
    days: p.new_duration,
    expiresAt: p.new_tier === 'free' ? null : p.new_expires_at || null,
    daysRemaining: p.days_remaining,
    remainingValue: p.remaining_value,
    trialDaysLost: p.trial_days_lost ?? 0,
    isUpgrade: p.is_upgrade,
  }
}

/** Состояние подписки: истечение срока статус на сервере не меняет, считаем по дате. */
export function subscriptionState(tier: string, status: string | undefined, expiresAt: string | undefined, now = new Date()): SubscriptionState {
  if (tier === 'free') return 'free'
  if (status === 'cancelled') return 'cancelled'
  if (status === 'expired') return 'expired'
  if (expiresAt && new Date(expiresAt).getTime() <= now.getTime()) return 'expired'
  return status === 'trial' ? 'trial' : 'active'
}

/** Ресурсы проверки превышения на сервере (grace) - в ключи экрана. */
const GRACE_KEY: Record<string, SubscriptionLimitKey> = {
  athletes: 'athletes',
  assistants: 'directions',
  groups: 'groups',
  coaches: 'coaches',
}

/** Обратная связь: слишком длинное сообщение - отдельный код, экран объясняет. */
function feedbackError(err: unknown): unknown {
  if (httpStatus(err) === 400 && httpError(err) === 'message too long') return new SliceApiError('too_long', 'server')
  return err
}

const FEEDBACK_CHANNELS: FeedbackChannel[] = ['telegram', 'max', 'app']
const feedbackChannel = (c: string | undefined): FeedbackChannel | undefined =>
  FEEDBACK_CHANNELS.find((x) => x === c)

export function toFeedbackSummary(d: FeedbackThreadSummaryDTO): FeedbackThreadSummary {
  return {
    id: d.id,
    subject: d.subject || '',
    status: d.status || 'new',
    createdAt: d.created_at,
    lastAt: d.last_at || d.created_at,
    lastFrom: d.last_from === 'support' ? 'support' : 'user',
    lastText: d.last_text || '',
    unread: Number(d.unread) || 0,
  }
}

export function toFeedbackMessage(d: FeedbackMessageDTO): FeedbackMessage {
  const platform = (['web', 'telegram', 'max'] as const).find((p) => p === d.platform)
  return {
    id: d.id,
    from: d.from === 'support' ? 'support' : 'user',
    text: d.text,
    createdAt: d.created_at,
    channel: feedbackChannel(d.channel),
    platform,
    notifyChannel: feedbackChannel(d.notify_channel),
    notifyStatus: d.notify_status,
    readAt: d.read_at || undefined,
  }
}

export function toFeedbackThread(d: FeedbackThreadDTO): FeedbackThread {
  return {
    id: d.id,
    subject: d.subject || '',
    status: d.status || 'new',
    createdAt: d.created_at,
    messages: arr(d.messages).map(toFeedbackMessage),
  }
}

async function guard<T>(call: () => Promise<T>): Promise<T> {
  if (!isOnline()) throw new SliceApiError('offline', 'network')
  try {
    return await call()
  } catch (err) {
    throw toSliceError(err)
  }
}

/**
 * Боевая реализация контракта среза поверх REST API.
 *
 * Экран занятия открывается чтением (`GET /training-sessions/board`), которое
 * ничего не создаёт. Строка занятия заводится первой отметкой через
 * `POST /training-sessions/resolve`; дальше отметки идут на её id.
 */
export function createLiveSliceApi(scope: () => LiveScope): SliceApi {
  // Ключ занятия -> id заведённого занятия, чтобы не дёргать resolve на каждую отметку.
  const sessionIds = new Map<string, string>()

  const remember = (key: string, board: SessionBoard) => {
    if (board.session_exists !== false && board.session_id) sessionIds.set(key, board.session_id)
    return board
  }

  /** Ключ занятия -> группа, дата и время; битый ключ - not_found. */
  const occurrenceOf = (key: string) => {
    const parsed = parseSessionKey(key)
    if (!parsed) throw new SliceApiError('bad session key', 'not_found')
    return parsed
  }

  /** Доска после отмены или возврата: из ответа, а без неё - прочитанная заново. */
  const boardAfter = async (
    key: string,
    o: { groupId: string; date: string; startTime: string },
    board: SessionBoard | null | undefined,
  ): Promise<SessionBoard> => {
    const b = board ?? (await trainingSessionsApi.peek(o.groupId, o.date, o.startTime))
    return remember(key, { ...b, roster: arr(b.roster) })
  }

  const requireOrg = () => {
    const { organizationId } = scope()
    if (!organizationId) throw new SliceApiError('no club selected', 'not_found')
    return organizationId
  }

  const requireAssistant = () => {
    const { assistantId } = scope()
    if (!assistantId) throw new SliceApiError('no direction selected', 'not_found')
    return assistantId
  }

  /**
   * Ждём конкретный ответ. Сначала читаем его по id: при повторе отправки он
   * может быть уже готов. Дальше long poll от его же updated_at (часы сервера,
   * не клиента), курсор сдвигается по всему, что пришло. Пустой ответ poll -
   * таймаут сервера: перепроверяем по id. Опрос идёт, только пока экран ждёт.
   */
  const waitReply = async (assistantMessageId: string, signal?: AbortSignal): Promise<HubMessage> => {
    const assistantId = requireAssistant()
    const deadline = Date.now() + HUB_REPLY_WAIT_MS
    let current = await chatApi.getMessage(assistantMessageId)
    let cursor = Date.parse(current.updated_at || current.created_at) || 0
    let failures = 0

    while (!replyReady(current)) {
      if (signal?.aborted) throw abortError()
      if (Date.now() > deadline) throw new SliceApiError('reply not ready', 'network')
      try {
        const items = arr(await chatApi.pollForNewMessages(assistantId, cursor, signal, HUB_CHANNEL))
        failures = 0
        for (const m of items) cursor = Math.max(cursor, Date.parse(m.updated_at || m.created_at) || 0)
        const hit = items.find((m) => m.id === assistantMessageId)
        if (hit) current = hit
        else if (items.length === 0) current = await chatApi.getMessage(assistantMessageId)
      } catch (err) {
        if (signal?.aborted) throw abortError()
        failures += 1
        if (failures >= 3) throw err
        await sleep(1000 * 2 ** failures, signal)
      }
    }
    return toHubMessage(current)
  }

  return {
    getContext: () =>
      guard(async () => {
        const { organizationId } = scope()
        const assistantId = requireAssistant()
        const [org, assistant] = await Promise.all([
          organizationId ? getOrganization(organizationId) : Promise.resolve(null),
          assistantsApi.getById(assistantId),
        ])
        return { clubName: org?.name ?? '', directionName: assistant.name, sportName: assistant.sport_name ?? '' }
      }),

    getSessions: (from, to) =>
      guard(async () => {
        const assistantId = requireAssistant()
        const end = parseIsoDate(to)
        // В API календаря "to" не входит в период.
        const toExclusive = end ? isoDate(addDays(end, 1)) : to
        const { items } = await calendarApi.getCalendar(assistantId, { from, to: toExclusive, type: 'trainings' })
        const slots: SessionSlot[] = arr(items)
          .filter((i) => i.type === 'training' && i.group_id)
          .map((i) => ({
            key: sessionKey(i.group_id!, i.date, i.start_time),
            groupId: i.group_id!,
            groupName: i.group_name ?? i.title,
            date: i.date,
            startTime: i.start_time,
            endTime: i.end_time,
            athleteCount: i.athlete_count ?? 0,
            cancelled: !!i.cancelled,
            extra: !!i.extra,
          }))
        return slots.sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime))
      }),

    getBoard: (key) =>
      guard(async () => {
        const parsed = parseSessionKey(key)
        if (!parsed) throw new SliceApiError('bad session key', 'not_found')
        const board = await trainingSessionsApi.peek(parsed.groupId, parsed.date, parsed.startTime)
        return remember(key, { ...board, roster: arr(board.roster) })
      }),

    mark: (key, athleteId, status) =>
      guard(async () => {
        const parsed = parseSessionKey(key)
        if (!parsed) throw new SliceApiError('bad session key', 'not_found')

        let sessionId = sessionIds.get(key)
        if (!sessionId) {
          const created = await trainingSessionsApi.resolve({
            assistant_id: requireAssistant(),
            group_id: parsed.groupId,
            date: parsed.date,
            start_time: parsed.startTime || undefined,
          })
          sessionId = remember(key, created).session_id
        }

        const board = status
          ? await trainingSessionsApi.mark(sessionId, athleteId, status)
          : await trainingSessionsApi.unmark(sessionId, athleteId)
        return remember(key, { ...board, roster: arr(board.roster) })
      }),

    listGroups: () =>
      guard(async () => {
        const groups = arr(await groupsApi.getByAssistantId(requireAssistant()))
        return groups.map((g) => ({ id: g.id, name: g.name }))
      }),

    listAthletes: () =>
      guard(async () => {
        const list = arr(await athletesApi.getByAssistantId(requireAssistant()))
        return [...list].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      }),

    getAthlete: (id) => guard(() => athletesApi.getById(id)),

    createAthlete: (input) =>
      guard(() =>
        athletesApi.create({
          assistant_id: requireAssistant(),
          name: input.name,
          date_of_birth: input.dateOfBirth || undefined,
          group_id: input.groupId || undefined,
          parent_phone: input.parentPhone || undefined,
        }),
      ),

    findSimilarAthletes: (name) =>
      guard(async () => [...arr(await athletesApi.findSimilar(requireAssistant(), name.trim()))]),

    updateAthlete: (id, patch) => guard(() => athletesApi.update(id, patch as UpdateAthleteRequest)),

    deleteAthlete: (id) => guard(() => athletesApi.delete(id)),

    getSportFields: (sportName) =>
      guard(async () => {
        if (!sportName) return []
        const fields = arr(await sportTypesApi.getFieldsBySportName(sportName))
        return [...fields].sort((a, b) => a.display_order - b.display_order)
      }),

    getAthleteAttendance: (id, from, to) =>
      guard(async () => {
        const [items, stats] = await Promise.all([
          attendanceApi.getByAthleteAndDateRange(id, from, to),
          attendanceApi.getStats(id, from, to),
        ])
        return { items: [...arr(items)].sort((a, b) => b.date.localeCompare(a.date)), stats }
      }),

    listAthleteNotes: (id) =>
      guard(async () => {
        const res = await athleteNotesApi.getByAthleteId(id, { limit: 100 })
        return arr(res?.notes).map(toNote)
      }),

    addAthleteNote: (id, input) =>
      guard(async () =>
        toNote(
          await athleteNotesApi.create(id, {
            assistant_id: requireAssistant(),
            title: input.title,
            content: input.content,
            note_type: input.noteType as AthleteNoteType,
          }),
        ),
      ),

    getGroup: (id) =>
      guard(async () => {
        const [g, coaches] = await Promise.all([groupsApi.getById(id), coachesApi.getByGroupId(id).catch(() => null)])
        return {
          id: g.id,
          name: g.name,
          schedule: parseGroupSchedule(g.schedule),
          coaches: arr(coaches).map((c) => ({ id: c.id, name: c.name })),
          description: g.description ?? '',
        }
      }),

    listGroupAthletes: (id) =>
      guard(async () => {
        const list = arr(await athletesApi.getByGroupId(id))
        return [...list].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      }),

    getGroupJournal: (id, from, to) =>
      guard(async () => {
        const j = await trainingSessionsApi.journal(id, from, to)
        return {
          ...j,
          sessions: arr(j.sessions),
          rows: arr(j.rows).map((r) => ({ ...r, marks: r.marks ?? {} })),
        }
      }),

    getGroupPayments: (id, yearMonth) =>
      guard(async () => {
        const p = await monthlyPaymentsApi.getGroupMonthly(id, yearMonth)
        return { ...p, athletes: arr(p.athletes), paused: arr(p.paused) }
      }),

    setPayment: (groupId, athleteId, yearMonth, paid) =>
      guard(async () => {
        await monthlyPaymentsApi.togglePayment(groupId, athleteId, yearMonth, paid)
      }),

    previewGroupSchedule: (groupId, schedule, from) =>
      guard(async () => toSchedulePreview(await groupsApi.previewSchedule(groupId, schedule, from))),

    saveGroupSchedule: (groupId, req) =>
      guard(async () => {
        const res = await groupsApi.replaceSchedule(groupId, {
          schedule: req.schedule,
          expected_schedule: req.expected,
          from: req.from,
          delete_session_ids: req.deleteSessionIds,
          replace_version_ids: req.replaceVersionIds,
        })
        return { replayed: !!res.replayed, deleted: res.deleted ?? 0, keptMarked: res.kept_marked ?? 0 }
      }),

    getGroupScheduleTimeline: (groupId, today) =>
      guard(async () => toScheduleTimeline(await groupsApi.getScheduleTimeline(groupId, today))),

    previewOccurrence: (key, change) =>
      guard(async () => {
        const o = occurrenceOf(key)
        const res = await occurrencesApi.preview(o.groupId, {
          date: o.date,
          start_time: o.startTime,
          action: change.action,
          ...(change.action === 'move'
            ? { to_date: change.toDate, to_start: change.toStart, ...(change.toEnd ? { to_end: change.toEnd } : {}) }
            : {}),
        })
        return toOccurrencePreview(res)
      }),

    // Отмена и возврат меняют строку занятия (заводят её или снимают отмену):
    // запомненный id для отметок берём из ответа, а не из прежнего кэша.
    cancelOccurrence: (key) =>
      guard(async () => {
        const o = occurrenceOf(key)
        const res = await occurrencesApi.cancel(o.groupId, { date: o.date, start_time: o.startTime })
        sessionIds.delete(key)
        return { replayed: !!res.replayed, board: await boardAfter(key, o, res.board) }
      }),

    restoreOccurrence: (key) =>
      guard(async () => {
        const o = occurrenceOf(key)
        const res = await occurrencesApi.restore(o.groupId, { date: o.date, start_time: o.startTime })
        sessionIds.delete(key)
        return { replayed: !!res.replayed, board: await boardAfter(key, o, res.board) }
      }),

    moveOccurrence: (key, to) =>
      guard(async () => {
        const o = occurrenceOf(key)
        const res = await occurrencesApi.move(o.groupId, {
          date: o.date,
          start_time: o.startTime,
          to_date: to.date,
          to_start: to.startTime,
          ...(to.endTime ? { to_end: to.endTime } : {}),
        })
        // Исходное занятие отменено, новое заведено (или возвращено из отмены):
        // по обоим ключам id перечитается с доски, а не берётся из кэша.
        const toKey = sessionKey(o.groupId, res.to?.date ?? to.date, res.to?.start_time ?? to.startTime)
        sessionIds.delete(key)
        sessionIds.delete(toKey)
        return { replayed: !!res.replayed, toKey }
      }),

    // Перекличка текстом и голосом (этап 5, шаг 2): короткая пишется сразу,
    // массовая - через confirmAiAction. Занятие задаёт ключ экрана, а не фраза.
    previewRollCall: (key, text) =>
      guard(async () => {
        const o = occurrenceOf(key)
        return toRollCallPreview(
          await trainingSessionsApi.previewRollCallText({
            group_id: o.groupId,
            date: o.date,
            start_time: o.startTime || undefined,
            text,
          }),
        )
      }),

    transcribeRollCall: (key, audio) =>
      guard(async () => {
        const o = occurrenceOf(key)
        // group_id сужает подсказку распознавания до состава группы занятия
        const res = await transcribeApi.transcribe(requireAssistant(), audio, o.groupId)
        return (res?.text ?? '').trim()
      }),

    getClubOverview: () =>
      guard(async () => {
        const orgId = requireOrg()
        const { assistantId, userId } = scope()
        const [org, orgsRaw, directionsRaw, membersRaw] = await Promise.all([
          getOrganization(orgId),
          getUserOrganizations(),
          assistantsApi.getByOrganizationId(orgId),
          getMembers(orgId).catch(() => null),
        ])
        const [orgs, directions, members] = [arr(orgsRaw), arr(directionsRaw), arr(membersRaw)]
        const me = members.find((m) => m.user_id === userId)
        return {
          club: { id: org.id, name: org.name, role: me?.role ?? (org.owner_id === userId ? 'owner' : 'admin') },
          clubs: orgs.map((o) => ({ id: o.id, name: o.name })),
          directions: directions.map((d) => ({
            id: d.id,
            name: d.name,
            sportName: d.sport_name ?? '',
            current: d.id === assistantId,
          })),
        }
      }),

    // Переключение идёт через тот же стор, что у прежнего интерфейса: выбор
    // сохраняется на сервере, LiveRedesign перечитывает данные по смене.
    switchClub: (id) => guard(() => useAuthStore.getState().switchToOrganization(id)),
    switchDirection: (id) => guard(() => useAuthStore.getState().syncCurrentAssistant(id)),

    getChannels: () =>
      guard(async () => {
        const { data } = await apiClient.get<Channels>('/users/me/channels')
        return data
      }),

    createLinkCode: () =>
      guard(async () => {
        const { data } = await apiClient.post<{ code: string; expires_in?: string }>('/auth/link-code/generate')
        return { code: data.code, expiresInMinutes: Math.round(Number(data.expires_in ?? 600) / 60) }
      }),

    getTeam: () =>
      guard(async () => {
        const orgId = requireOrg()
        const { userId } = scope()
        const members = arr(await getMembers(orgId))
        const me = members.find((m) => m.user_id === userId)
        const canManage = me?.role === 'owner'
        // Список приглашений видит только владелец: остальным сервер откажет.
        const invites = canManage ? arr(await getInvites(orgId).catch(() => null)) : []
        return {
          canManage,
          members: members.map((m) => ({
            userId: m.user_id,
            name: m.user_name || 'Без имени',
            role: m.role,
            isMe: m.user_id === userId,
          })),
          invites: invites
            .filter((i) => !i.revoked_at)
            .map((i) => ({
              id: i.id,
              code: i.code,
              expiresAt: i.expires_at,
              remainingUses: i.remaining_uses,
              active: i.is_active && !i.is_expired,
              expired: i.is_expired,
            })),
        }
      }),

    createInvite: (validDays, maxUses) =>
      guard(async () => {
        // Роль пока одна: сервер принимает только admin (см. инвентаризацию).
        const invite = await generateInvite(requireOrg(), { role: 'admin', valid_days: validDays, max_uses: maxUses })
        return { code: invite.code }
      }),

    revokeInvite: (id) => guard(() => revokeInvite(requireOrg(), id)),

    renameClub: (name) =>
      guard(async () => {
        await updateOrganization(requireOrg(), { name })
      }),

    previewInvite: (code) =>
      guard(async () => {
        const invite = await validateInvite(code.trim().toUpperCase()).catch((err) => {
          throw inviteError(err)
        })
        const withOrg = invite as typeof invite & { organization_name?: string }
        return { clubName: withOrg.organization_name ?? 'клуб по приглашению' }
      }),

    acceptInvite: (code) =>
      guard(async () => {
        const normalized = code.trim().toUpperCase()
        let organizationId: string
        try {
          const res = await acceptInviteCode({ code: normalized })
          organizationId = res.organization.id
        } catch (err) {
          if (httpStatus(err) !== 409 || httpError(err) !== 'already a member') throw inviteError(err)
          // Участник мог переключиться на пустой личный клуб. Повторное
          // приглашение возвращает его в существующий, не меняя роль и лимит.
          const invite = await validateInvite(normalized).catch((error) => { throw inviteError(error) })
          organizationId = invite.organization_id
        }
        await useAuthStore.getState().switchToOrganization(organizationId)
      }),

    listSportTypes: () =>
      guard(async () => {
        const list = arr(await sportTypesListApi.list())
        return list.map((s) => ({ name: s.name, kind: s.sport_type }))
      }),

    createDirection: (name, sport) =>
      guard(async () => {
        const { userId } = scope()
        if (!userId) throw new SliceApiError('no user', 'not_found')
        let created: Awaited<ReturnType<typeof assistantsApi.create>>
        try {
          created = await assistantsApi.create({
            user_id: userId,
            organization_id: requireOrg(),
            name,
            sport_type: sport.kind || 'individual',
            sport_name: sport.name,
          })
        } catch (err) {
          // Лимит направлений тарифа сервер отдаёт 403 с кодом (этап 5 шаг 4)
          if (httpStatus(err) === 403 && (err as { code?: string }).code === 'ERR_LIMIT_EXCEEDED') {
            throw new SliceApiError('direction_limit_reached', 'limit')
          }
          throw err
        }
        await useAuthStore.getState().syncCurrentAssistant(created.id)
        return { id: created.id }
      }),

    createGroup: (name, schedule, description) =>
      guard(async () => {
        const assistantId = requireAssistant()
        const g = await groupsApi.create(assistantId, {
          assistant_id: assistantId,
          name,
          description: description?.trim() || undefined,
          schedule: schedule.length > 0 ? JSON.stringify(schedule) : undefined,
        } as Parameters<typeof groupsApi.create>[1])
        return { id: g.id }
      }),

    getSubscription: () =>
      guard(async () => {
        const { userId } = scope()
        if (!userId) throw new SliceApiError('no user', 'not_found')
        const orgId = requireOrg()
        const [info, org, membersRaw, discount] = await Promise.all([
          subscriptionApi.getInfo(userId),
          getOrganization(orgId),
          // Участники и скидка - дополнение: их сбой не прячет тариф и лимиты
          getMembers(orgId).catch(() => null),
          promoCodesApi.getActiveDiscount().catch(() => null),
        ])
        const limits = info.Limits as typeof info.Limits & { MaxMembers?: number; MaxAIRequests?: number }
        const usage = info.Usage as typeof info.Usage & { AIQuota?: number }
        const rows: { key: SubscriptionLimitKey; used: number; max: number }[] = []
        if (membersRaw && typeof limits.MaxMembers === 'number') {
          rows.push({ key: 'members', used: arr(membersRaw).length, max: limits.MaxMembers })
        }
        rows.push(
          { key: 'directions', used: usage.Assistants, max: limits.MaxAssistants },
          { key: 'athletes', used: usage.Athletes, max: limits.MaxAthletes },
          { key: 'groups', used: usage.Groups, max: limits.MaxGroups },
          { key: 'coaches', used: usage.Coaches, max: limits.MaxCoaches },
        )
        if (typeof limits.MaxAIRequests === 'number') {
          rows.push({ key: 'ai', used: usage.AIQuota ?? 0, max: limits.MaxAIRequests })
        }
        const grace = info.GraceStatus
        const expiresAt = org.subscription_expires_at || info.ExpiresAt || undefined
        return {
          tier: info.Tier,
          status: org.subscription_status ?? info.Status,
          expiresAt,
          limits: rows,
          paidTier: org.subscription_tier,
          state: subscriptionState(org.subscription_tier, org.subscription_status, expiresAt),
          autoRenewal: !!org.auto_renewal,
          cardSaved: !!org.payment_method_saved,
          trialUsed: !!org.trial_used,
          canManage: org.owner_id === userId,
          grace:
            grace && grace.is_over_limit
              ? {
                  softLocked: !!grace.is_soft_locked,
                  daysLeft: grace.grace_period_days_remaining ?? 0,
                  exceeded: Object.keys(grace.exceeded_resources ?? {})
                    .map((k) => GRACE_KEY[k])
                    .filter((k): k is SubscriptionLimitKey => !!k),
                }
              : null,
          discount:
            discount?.has_active_discount && (discount.discount_percent ?? 0) > 0
              ? { percent: discount.discount_percent ?? 0, code: discount.promo_code, expiresAt: discount.discount_expires_at }
              : null,
        }
      }),

    previewTierChange: (tier) =>
      guard(async () => {
        try {
          return toTierPreview(await tierChangeApi.preview(requireOrg(), tier))
        } catch (err) {
          throw tierError(err)
        }
      }),

    applyFreeTierChange: (tier) =>
      guard(async () => {
        const orgId = requireOrg()
        try {
          // Сервер считает смену заново и берёт из запроса только новый тариф;
          // расчёт передаём целиком ради совместимости с прежним форматом.
          const preview = await tierChangeApi.preview(orgId, tier)
          if (preview.charge_amount > 0) throw new SliceApiError('payment_required', 'conflict')
          await tierChangeApi.confirm(orgId, preview)
        } catch (err) {
          throw tierError(err)
        }
      }),

    createTierPayment: (tier, opts) =>
      guard(async () => {
        const orgId = requireOrg()
        try {
          const res = await tierChangeApi.createPayment({
            organization_id: orgId,
            new_tier: tier,
            auto_renewal: opts.autoRenewal,
            return_url: opts.returnUrl,
          })
          return { paymentId: res.payment_id, confirmationUrl: res.confirmation_url, reused: !!res.reused }
        } catch (err) {
          // Ручки оплаты нет, пока на сервере не подключена платёжная система
          if (httpStatus(err) === 404) throw new SliceApiError('payments_unavailable', 'server')
          throw tierError(err)
        }
      }),

    getPayment: (id, withProvider) =>
      guard(async () =>
        toPaymentInfo(
          withProvider ? await tierChangeApi.checkPaymentWithProvider(id) : await tierChangeApi.getPaymentStatus(id),
        ),
      ),

    listPayments: () =>
      guard(async () => {
        try {
          const { data } = await apiClient.get<{ payments: Parameters<typeof toPaymentInfo>[0][] | null }>(
            '/payments/subscriptions',
          )
          return { available: true, items: arr(data?.payments).map(toPaymentInfo) }
        } catch (err) {
          if (httpStatus(err) === 404) return { available: false, items: [] }
          throw err
        }
      }),

    setAutoRenewal: (on) =>
      guard(async () => {
        try {
          await apiClient.put(`/organizations/${requireOrg()}/auto-renewal`, { auto_renewal: on })
        } catch (err) {
          throw tierError(err)
        }
      }),

    redeemPromo: (code) =>
      guard(async () => {
        const trimmed = code.trim()
        const check = await promoCodesApi.validate(trimmed)
        if (!check.valid) throw new SliceApiError(check.reason || 'Промокод не подошёл', 'conflict')
        try {
          const res = await promoCodesApi.redeem(trimmed)
          return { percent: res.discount_percent, expiresAt: res.discount_expires_at }
        } catch (err) {
          const status = httpStatus(err)
          if (status === 404 || status === 409) throw new SliceApiError('Промокод не подошёл', 'conflict')
          throw err
        }
      }),

    getHubHistory: () =>
      guard(async () => {
        const list = arr(await chatApi.getHistory(requireAssistant(), 50, HUB_CHANNEL))
        return list.map(toHubMessage)
      }),

    sendHubMessage: (text, clientMessageId, assistantId) =>
      guard(async () => {
        const res = await chatApi.enqueueMessage(assistantId || requireAssistant(), text, clientMessageId)
        return {
          userMessageId: res.user_message_id,
          assistantMessageId: res.assistant_message_id,
          replayed: !!res.replayed,
        }
      }),

    waitHubReply: async (assistantMessageId, signal) => {
      try {
        return await waitReply(assistantMessageId, signal)
      } catch (err) {
        // Уход с экрана - не ошибка данных: отдаём AbortError, а не SliceApiError
        if (signal?.aborted) throw abortError()
        throw toSliceError(err)
      }
    },

    transcribeVoice: (audio, assistantId) =>
      guard(async () => {
        const res = await transcribeApi.transcribe(assistantId || requireAssistant(), audio)
        return (res?.text ?? '').trim()
      }),

    getAiAction: (id) => guard(async () => toAiAction(await aiActionsApi.get(id))),

    confirmAiAction: (id) =>
      guard(async () => {
        const res = await aiActionsApi.confirm(id)
        return { action: toAiAction(res), replayed: !!res.replayed }
      }),

    cancelAiAction: (id) =>
      guard(async () => {
        const res = await aiActionsApi.cancel(id)
        return { action: toAiAction(res), replayed: !!res.replayed }
      }),

    createHubContextLink: (channel, ref) =>
      guard(async () => {
        const body: HubContextLinkRequest = { assistant_id: requireAssistant(), channel, kind: ref?.kind ?? 'none' }
        if (ref?.kind === 'group') body.group_id = ref.groupId
        if (ref?.kind === 'athlete') body.athlete_id = ref.athleteId
        if (ref?.kind === 'session') {
          body.group_id = ref.groupId
          body.date = ref.date
          if (ref.startTime) body.start_time = ref.startTime
        }
        const res = await hubContextApi.create(body)
        return { startParam: res.start_param, expiresAt: res.expires_at, label: res.label ?? '' }
      }),

    listEvents: () =>
      guard(async () => {
        const list = arr(await eventsApi.getByAssistantId(requireAssistant()))
        return list.map(toClubEvent).filter((e): e is ClubEvent => e !== null)
      }),

    getEvent: (id) =>
      guard(async () => {
        const e = toClubEvent(await eventsApi.getById(id))
        if (!e) throw new SliceApiError('bad event date', 'server')
        return e
      }),

    createEvent: (input) =>
      guard(async () => {
        const e = toClubEvent(await eventsApi.create(requireAssistant(), eventBody(input)))
        if (!e) throw new SliceApiError('bad event date', 'server')
        return e
      }),

    // PUT заменяет место и описание всегда, а пустые окончание и взнос
    // считает «не менять»: стереть их можно только явными флагами.
    updateEvent: (id, input) =>
      guard(async () => {
        const body = eventBody(input)
        const e = toClubEvent(
          await eventsApi.update(id, {
            ...body,
            clear_end_date: body.end_date ? undefined : true,
            clear_payment_amount: body.payment_amount ? undefined : true,
          }),
        )
        if (!e) throw new SliceApiError('bad event date', 'server')
        return e
      }),

    deleteEvent: (id) => guard(() => eventsApi.delete(id)),

    listEventParticipants: (id) =>
      guard(async () => {
        const list = arr(await eventsApi.getParticipants(id))
        return list.map(toParticipant).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      }),

    addEventParticipants: (eventId, athleteIds) =>
      guard(async () => {
        const res = await eventsApi.bulkAddParticipants(eventId, { athlete_ids: athleteIds })
        return {
          added: arr(res.added_ids),
          already: arr(res.already_ids),
          failed: arr(res.errors).length,
        }
      }),

    updateEventParticipant: (eventId, athleteId, patch) =>
      guard(async () => {
        await eventsApi.updateParticipant(eventId, athleteId, {
          ...(patch.status ? { status: patch.status } : {}),
          ...(patch.payment ? { payment_status: patch.payment } : {}),
          // Результат - JSON-строкой, как его пишет Штаб; '' стирает.
          ...(patch.result !== undefined ? { result: patch.result.trim() } : {}),
        })
      }),

    removeEventParticipant: (eventId, athleteId) => guard(() => eventsApi.removeParticipant(eventId, athleteId)),

    listAthleteEvents: (athleteId) =>
      guard(async () => {
        const list = arr(await eventsApi.getAthleteEvents(athleteId))
        return list.flatMap((a) => {
          const event = toClubEvent(a)
          if (!event) return []
          return [
            {
              event,
              status: PARTICIPATION.find((s) => s === a.participation_status) ?? 'registered',
              payment: PAYMENTS.find((s) => s === a.participant_payment_status) ?? 'not_paid',
              result: resultText(a.result) || undefined,
            },
          ]
        })
      }),

    // Группы целиком (этап 5 шаг 1)
    updateGroup: (id, patch) =>
      guard(async () => {
        // Пустое описание сервер считает «не менять»: стереть - только явным флагом.
        const clear = patch.description !== undefined && patch.description.trim() === ''
        await groupsApi.update(id, {
          name: patch.name?.trim() || undefined,
          description: clear ? undefined : patch.description?.trim(),
          ...(clear ? { clear_description: true } : {}),
        })
      }),

    deleteGroup: (id) => guard(() => groupsApi.delete(id)),

    listCoaches: () =>
      guard(async () => {
        const list = arr(await coachesApi.getByAssistantId(requireAssistant()))
        return list.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      }),

    createCoach: (name) =>
      guard(async () => {
        const c = await coachesApi.create(requireAssistant(), { name: name.trim() })
        return { id: c.id, name: c.name }
      }),

    setGroupCoach: (groupId, coachId, assigned) =>
      guard(() =>
        assigned ? coachesApi.assignToGroup(coachId, { group_id: groupId }) : coachesApi.removeFromGroup(coachId, groupId),
      ),

    // ---------- записи и материалы (этап 5 шаг 3) ----------

    updateAthleteNote: (athleteId, noteId, input) =>
      guard(async () =>
        toNote(
          await athleteNotesApi.update(athleteId, noteId, {
            title: input.title,
            content: input.content,
            note_type: input.noteType as AthleteNoteType,
          }),
        ),
      ),

    deleteAthleteNote: (athleteId, noteId) => guard(() => athleteNotesApi.delete(athleteId, noteId)),

    listAthleteNotesTrash: (athleteId) => guard(async () => arr(await athleteNotesApi.trash(athleteId)).map(toDeletedNote)),

    restoreAthleteNote: (athleteId, noteId) => guard(async () => toNote(await athleteNotesApi.restore(athleteId, noteId))),

    listGroupNotes: (groupId) =>
      guard(async () => {
        const res = await groupNotesApi.getByGroupId(groupId, { limit: 100 })
        return arr(res?.notes).map(toNote)
      }),

    addGroupNote: (groupId, input) =>
      guard(async () =>
        toNote(
          await groupNotesApi.create(groupId, {
            assistant_id: requireAssistant(),
            title: input.title,
            content: input.content,
            note_type: input.noteType as GroupNoteType,
          }),
        ),
      ),

    updateGroupNote: (groupId, noteId, input) =>
      guard(async () =>
        toNote(
          await groupNotesApi.update(groupId, noteId, {
            title: input.title,
            content: input.content,
            note_type: input.noteType as GroupNoteType,
          }),
        ),
      ),

    deleteGroupNote: (groupId, noteId) => guard(() => groupNotesApi.delete(groupId, noteId)),

    listGroupNotesTrash: (groupId) => guard(async () => arr(await groupNotesApi.trash(groupId)).map(toDeletedNote)),

    restoreGroupNote: (groupId, noteId) => guard(async () => toNote(await groupNotesApi.restore(groupId, noteId))),

    listMaterials: () => guard(async () => arr(await materialsApi.getByAssistantId(requireAssistant())).map(toMaterial)),

    getMaterial: (id) => guard(async () => toMaterial(await materialsApi.getById(id))),

    createMaterial: (input) =>
      guard(async () =>
        toMaterial(
          await materialsApi.create(requireAssistant(), {
            title: input.title,
            content: input.content,
            category: input.category,
            tags: input.tags,
            sort_order: input.sortOrder ?? 0,
          }),
        ),
      ),

    // Сервер ставит порядок из запроса: без прежнего sort_order материал
    // съехал бы в начало раздела.
    updateMaterial: (id, input) =>
      guard(async () =>
        toMaterial(
          await materialsApi.update(id, {
            title: input.title,
            content: input.content,
            category: input.category,
            tags: input.tags,
            sort_order: input.sortOrder ?? 0,
          }),
        ),
      ),

    deleteMaterial: (id) => guard(() => materialsApi.delete(id)),

    listMaterialsTrash: () =>
      guard(async () =>
        arr(await materialsApi.trash(requireAssistant())).map((m) => ({
          ...toMaterial(m),
          deleted_at: m.deleted_at ?? '',
          deleted_by_name: m.deleted_by_name || null,
          purge_at: m.purge_at ?? '',
        })),
      ),

    restoreMaterial: (id) => guard(async () => toMaterial(await materialsApi.restore(id))),
    // Направления, справочник тренеров, мелочи клуба (этап 5 шаг 4)
    updateDirection: (id, patch) =>
      guard(async () => {
        // Сервер пустые поля не меняет: уходит только изменённое.
        await assistantsApi.update(id, {
          name: patch.name?.trim() || undefined,
          sport_name: patch.sport?.name,
          sport_type: patch.sport ? patch.sport.kind || 'individual' : undefined,
        })
      }),

    getDirectionContents: (id) =>
      guard(async () => {
        const [groups, athletes, coaches, events] = await Promise.all([
          groupsApi.getByAssistantId(id),
          athletesApi.getByAssistantId(id),
          coachesApi.getByAssistantId(id),
          eventsApi.getByAssistantId(id),
        ])
        const list = arr(athletes)
        return {
          groups: arr(groups)
            .map((g) => ({ id: g.id, name: g.name, athletes: list.filter((a) => a.group_id === g.id).length }))
            .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
          athletes: list.map((a) => ({ id: a.id, name: a.name })).sort((a, b) => a.name.localeCompare(b.name, 'ru')),
          coaches: arr(coaches)
            .map((c) => ({ id: c.id, name: c.name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
          events: arr(events).length,
        }
      }),

    deleteDirection: (id) =>
      guard(async () => {
        const orgId = requireOrg()
        let alreadyGone = false
        try {
          await assistantsApi.delete(id)
        } catch (err) {
          // Повтор после потерянного ответа: направление уже удалено, но текущим
          // оно могло остаться - переключение ниже всё равно нужно.
          if (httpStatus(err) !== 404) throw err
          alreadyGone = true
        }
        if (scope().assistantId === id) {
          // Удалено текущее: текущим - первое оставшееся, а если их нет -
          // никакое (LiveRedesign откроет мастер).
          const rest = arr(await assistantsApi.getByOrganizationId(orgId)).filter((d) => d.id !== id)
          await useAuthStore.getState().syncCurrentAssistant(rest[0]?.id ?? null)
        }
        if (alreadyGone) throw new SliceApiError('not found', 'not_found')
      }),

    listCoachCards: () =>
      guard(async () => {
        const list = arr(await coachesApi.getByAssistantId(requireAssistant()))
        const cards = await Promise.all(list.map((c) => coachCard(c)))
        return cards.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      }),

    getCoach: (id) => guard(async () => coachCard(await coachesApi.getById(id))),

    createCoachCard: (input) =>
      guard(async () => {
        const c = await coachesApi.create(requireAssistant(), {
          name: input.name.trim(),
          phone: input.phone?.trim() || undefined,
          email: input.email?.trim() || undefined,
        })
        return { id: c.id, name: c.name }
      }),

    updateCoach: (id, patch) =>
      guard(async () => {
        // Пустое сервер считает «не менять»: стереть - явным флагом.
        const clearPhone = patch.phone !== undefined && patch.phone.trim() === ''
        const clearEmail = patch.email !== undefined && patch.email.trim() === ''
        await coachesApi.update(id, {
          name: patch.name?.trim() || undefined,
          phone: clearPhone ? undefined : patch.phone?.trim(),
          email: clearEmail ? undefined : patch.email?.trim(),
          ...(clearPhone ? { clear_phone: true } : {}),
          ...(clearEmail ? { clear_email: true } : {}),
        } as Parameters<typeof coachesApi.update>[1])
      }),

    deleteCoach: (id) => guard(() => coachesApi.delete(id)),

    leaveClub: () => guard(() => leaveOrDelete((orgId) => leaveOrganization(orgId))),

    deleteClub: () => guard(() => leaveOrDelete((orgId) => deleteOrganization(orgId))),

    // ---------- обратная связь как переписка ----------
    listFeedbackThreads: () =>
      guard(async () =>
        arr(await feedbackApi.listThreads())
          .map(toFeedbackSummary)
          .sort((a, b) => b.lastAt.localeCompare(a.lastAt)),
      ),

    getFeedbackThread: (id) => guard(async () => toFeedbackThread(await feedbackApi.getThread(id))),

    createFeedbackThread: (message, from) =>
      guard(async () => {
        try {
          return toFeedbackThread(await feedbackApi.createThread(message.trim(), from))
        } catch (err) {
          throw feedbackError(err)
        }
      }),

    replyFeedbackThread: (id, message, from) =>
      guard(async () => {
        try {
          return toFeedbackMessage(await feedbackApi.reply(id, message.trim(), from))
        } catch (err) {
          throw feedbackError(err)
        }
      }),

    markFeedbackRead: (id) =>
      guard(async () => {
        await feedbackApi.markRead(id)
      }),
  }

  /** Тренер с группами, которые он ведёт (удалённые группы сервер не отдаёт). */
  async function coachCard(c: { id: string; name: string; phone?: string; email?: string }): Promise<CoachCard> {
    const groups = arr(await coachesApi.getGroupsByCoachId(c.id))
    return {
      id: c.id,
      name: c.name,
      phone: c.phone ?? '',
      email: c.email ?? '',
      groups: groups.map((g) => ({ id: g.id, name: g.name })).sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    }
  }

  /**
   * Выйти из клуба или удалить его, затем перейти в другой свой клуб. Сервер
   * сам переключает выбранный клуб; стор приложения переключается вслед.
   */
  async function leaveOrDelete(call: (orgId: string) => Promise<void>): Promise<{ switchedTo: string }> {
    const orgId = requireOrg()
    const next = arr(await getUserOrganizations()).find((o) => o.id !== orgId)
    if (!next) throw new SliceApiError('last_club', 'conflict')
    try {
      await call(orgId)
    } catch (err) {
      if (httpStatus(err) === 409 && httpError(err) === 'last organization') throw new SliceApiError('last_club', 'conflict')
      if (httpStatus(err) === 400 && httpError(err) === 'cannot delete organization with members') {
        throw new SliceApiError('has_members', 'conflict')
      }
      if (httpStatus(err) === 403) throw new SliceApiError('forbidden', 'server')
      // Повтор после потерянного ответа: из клуба уже вышли - охрана отвечает 404
      if (httpStatus(err) !== 404) throw err
    }
    await useAuthStore.getState().switchToOrganization(next.id)
    return { switchedTo: next.name }
  }
}
